// @ts-nocheck
import { viIncludes } from "@/lib/utils";
import React from "react";
import { useState, useMemo } from "react";
import { useHeaderSearch } from "@/hooks/useHeaderSearch";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import {
  Plus,
  Download,
  MoreHorizontal,
  FileText,
  Edit,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Trash2,
  PackageCheck,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  AlertOctagon,
  Layers,
  ShieldCheck,
} from "lucide-react";
import { PendingIqcQueueTab } from "./components/PendingIqcQueueTab";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn, formatNumber } from "@/lib/utils";
import {
  BulkActionBar,
  type BulkAction,
} from "@/components/shared/BulkActionBar";
import { toast } from "sonner";
import { useExcelExport } from "@/hooks/useExcelExport";
import type { ExcelColumnDef } from "@/lib/excel.utils";
import { trpcClient } from "@/lib/trpc";
import { QCMaterialForm } from "./QCMaterialForm";
import type { QCMaterialItem } from "@/shared/schemas";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import { type StatusSidebarItem } from "@/components/shared/StatusSidebar";
import { QCStatus, getStatusOptions } from "@/config/status-registry";
import {
  ListPageShell,
  useListFilter,
} from "@/components/shared/ListPageShell";
import {
  InlineFilterRow,
  type FilterField,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import {
  CompactKpiRibbon,
  type CompactKpiItem,
} from "@/components/shared/CompactKpiRibbon";

// ── Build sidebar items from registry ──
import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { useConfirm } from "@/providers/ConfirmProvider";
import { emsTabClass } from "@/components/ui/ems-tabs";

const QC_MATERIAL_FILTER_FIELDS: FilterField[] = [
  CHECKBOX_COL_NULL,
  { key: "id", label: "Lọc mã phiếu...", type: "text" },
  { key: "itemName", label: "Lọc tên / mã NVL...", type: "text" },
  { key: "sourceName", label: "Lọc nguồn gốc / NCC...", type: "text" },
  null,
  {
    key: "status",
    label: "Trạng thái",
    type: "select",
    options: getStatusOptions("QCStatus", false).filter(o =>
      ["PASSED", "WARNING", "FAILED", "PENDING"].includes(o.value)
    ),
  },
  null,
];

const STATUS_ITEMS: StatusSidebarItem[] = Object.entries(QCStatus).map(
  ([key, cfg]) => ({
    key,
    label: cfg.label,
    color: cfg.color,
    dotColor: cfg.dotColor,
  }),
);

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "materialQcId", label: "Mã phiếu", width: 16 },
  { key: "item_name", label: "Tên vật tư", width: 25 },
  { key: "item_code", label: "Mã vật tư", width: 16 },
  { key: "batch_no", label: "Số lô", width: 14 },
  { key: "type", label: "Loại", width: 14 },
  { key: "source_name", label: "Nguồn gốc", width: 20 },
  { key: "source_type", label: "Loại nguồn", width: 16 },
  { key: "aql_level", label: "Mức AQL", width: 14 },
  { key: "defects_major", label: "Lỗi nặng", width: 14, format: "number" },
  { key: "defects_minor", label: "Lỗi nhẹ", width: 14, format: "number" },
  { key: "defects_critical", label: "Lỗi nghiêm trọng", width: 16, format: "number" },
  { key: "status", label: "Trạng thái", width: 16 },
  {
    key: "checkTime",
    label: "Ngày kiểm",
    width: 14,
    formatter: (_v, row: any) => {
      const d = new Date(
        row.checkTime || row.check_time || row.createdAt,
      );
      return isNaN(d.getTime())
        ? ""
        : d.toLocaleDateString("vi-VN");
    },
  },
];

export default function QCMaterialsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { hasPermission } = useRoleAccess();

  const routerSearch = (useRouterState({ select: (s) => s.location.search }) as Record<string, any>) || {};
  const [activeMainTab, setActiveMainTab] = useState<"pending_iqc" | "qc_reports">(
    routerSearch?.tab === "pending_iqc" ? "pending_iqc" : "qc_reports"
  );

  const handleMainTabChange = (tab: "pending_iqc" | "qc_reports") => {
    setActiveMainTab(tab);
    navigate({
      search: (prev: any) => ({
        ...prev,
        tab: tab === "pending_iqc" ? "pending_iqc" : undefined,
      }),
      replace: true,
    });
  };

  const { data: tabCounts } = useQuery({
    queryKey: ["qc-materials-tab-counts"],
    // Quay lại danh sách sau khi xoá/sửa ở trang chi tiết luôn tải lại, không hiện bản cũ (FB-7KL5H3)
    refetchOnMount: "always",
    queryFn: () => trpcClient.qcMaterials.getTabCounts.query(),
  });

  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [editingCheck, setEditingCheck] = useState<any>(null);

  // Fetch ALL data for sidebar counts
  const { data, isLoading, error } = useQuery({
    queryKey: ["qc-materials-all"],
    refetchOnMount: "always",
    queryFn: async () => {
      const paginatedData = await trpcClient.qcMaterials.list.query({
        page: 1,
        limit: 100,
      });
      return paginatedData.items || [];
    },
  });

  // Fetch Stats for 4 KPI cards
  const { data: statsData } = useQuery({
    queryKey: ["qc-materials-stats"],
    queryFn: async () => (trpcClient as any).qcMaterials.getStats.query(),
  });

  const allItems: QCMaterialItem[] = data || [];

  const {
    filtered: filteredData,
    paginatedItems,
    totalItems,
    currentPage,
    pageSize,
    showFilter,
    filterValues,
    onFilterChange,
    onToggleFilter,
    onClearAll,
    onPageChange: setCurrentPage,
    onPageSizeChange: setPageSize,
  } = useListFilter({
    data: allItems,
    searchFields: ["id", "materialQcId", "item_code", "item_name", "batch_no", "source_name"],
    preFilter: (item: any) => {
      if (statusFilter && item.status !== statusFilter) return false;
      return true;
    },
  });

  const rowIds = paginatedItems.map((x) => x.id);
  const {
    selectedIds,
    selectionMode,
    toggleSelect,
    handleRowSelect,
    toggleSelectAll,
    handleRowMouseDown,
    handleRowMouseUp,
    handleRowMouseLeave,
    wasLongPress,
    allSelected,
    clearSelection,
    selectedCount,
  } = useRowSelection(rowIds);

  const { handleExport, isExporting } = useExcelExport({
    filename: "qc_nvl",
    columns: EXPORT_COLUMNS,
    localData: filteredData,
    selectedIds: Array.from(selectedIds),
  });

  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    const actions: BulkAction[] = [];
    actions.push({
      label: "Xuất Excel",
      icon: <Download className="w-3.5 h-3.5" />,
      onClick: () => {
        handleExport();
        clearSelection();
      },
    });
    if (hasPermission("qcMaterials", "delete")) {
      actions.push({
        label: "Xoá",
        icon: <Trash2 className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            actionFn: async (id) =>
              trpcClient.qcMaterials.delete.mutate({ id }),
            actionName: "xóa",
            itemType: "QC vật tư",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["qc-materials-all"] });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn xóa ${selectedCount} QC vật tư đã chọn?`,
          });
        },
        variant: "destructive",
      });
    }
    return actions;
  };

  const handleSave = async (data: any) => {
    try {
      if (editingCheck) {
        await trpcClient.qcMaterials.update.mutate({
          id: editingCheck.id,
          data,
        });
      } else {
        await trpcClient.qcMaterials.create.mutate(data);
      }
      setShowAddSheet(false);
      setEditingCheck(null);
      await queryClient.invalidateQueries({ queryKey: ["qc-materials-all"] });
    } catch (error: any) {
      toast.error(error?.message || "Không thể lưu phiếu QC");
    }
  };

  const stats = statsData || {
    total: allItems.length,
    passed: allItems.filter(
      (o: any) =>
        o.status?.toString()?.toUpperCase() === "PASSED" ||
        o.status === "pass",
    ).length,
    warning: allItems.filter(
      (o: any) =>
        o.status?.toString()?.toUpperCase() === "WARNING" ||
        o.status === "warning",
    ).length,
    failed: allItems.filter(
      (o: any) =>
        o.status?.toString()?.toUpperCase() === "FAILED" ||
        o.status === "fail",
    ).length,
  };

  const handleQuickCreateNcr = (item: any) => {
    navigate({
      to: "/ncr/create",
      search: {
        sourceType: "IQC",
        sourceId: item.id,
      } as any,
    });
  };

  const filterTags = React.useMemo(() => {
    return STATUS_ITEMS.map((item) => ({
      ...item,
      count:
        allItems?.filter(
          (o: any) =>
            o.status?.toString()?.toUpperCase() === item.key?.toUpperCase(),
        )?.length || 0,
    }));
  }, [allItems]);
  const qcMatKpiItems = useMemo<CompactKpiItem[]>(() => [
    {
      key: "ALL",
      label: "Tổng Lô IQC",
      count: stats.total,
      icon: PackageCheck,
      variant: "default",
      tooltip: "Vật tư nhập xưởng",
    },
    {
      key: "PASSED",
      label: "Đạt Chuẩn (Nhập Kho)",
      count: stats.passed,
      icon: CheckCircle2,
      variant: "emerald",
      tooltip: "Mở khóa khả dụng",
    },
    {
      key: "WARNING",
      label: "Cảnh Báo Độ Ẩm / GSM",
      count: stats.warning,
      icon: AlertTriangle,
      variant: "amber",
      tooltip: "Chờ KCS xác nhận",
      animate: stats.warning > 0,
    },
    {
      key: "FAILED",
      label: "Từ Chối / Phế Phẩm",
      count: stats.failed,
      icon: XCircle,
      variant: "rose",
      animate: stats.failed > 0,
      tooltip: "Yêu cầu trả hàng / NCR",
    },
  ], [stats]);

  const activeKpiKey = statusFilter || "ALL";

  const handleSelectKpi = (key: string | null) => {
    if (!key || key === "ALL") {
      setStatusFilter(null);
    } else {
      setStatusFilter(statusFilter === key ? null : key);
    }
  };

  const iqcFilterTags = useMemo(() => [
    { key: "ALL", label: "Tất cả kết quả", count: allItems.length, color: "slate" as const },
    { key: "PASSED", label: "Đạt chuẩn", count: stats.passed, color: "emerald" as const },
    { key: "WARNING", label: "Cảnh báo", count: stats.warning, color: "amber" as const },
    { key: "FAILED", label: "Từ chối", count: stats.failed, color: "rose" as const },
  ], [allItems.length, stats]);

  return (
    <>
      <ListPageShell
        trashModule="qc-materials"
        tabs={
          <div className="flex items-center gap-4">
            <button
              type="button"
              data-testid="tab-qc-reports"
              onClick={() => handleMainTabChange("qc_reports")}
              className={emsTabClass(activeMainTab === "qc_reports", { size: "sm" })}
            >
              <FileText className="w-3.5 h-3.5 text-indigo-600" />
              <span>Lịch Sử IQC</span>
              <span
                className={cn(
                  "px-1.5 py-0.2 rounded-full text-[10px] font-bold tabular-nums",
                  activeMainTab === "qc_reports"
                    ? "bg-primary/10 text-primary"
                    : "bg-slate-200 text-slate-600"
                )}
              >
                {tabCounts?.all ?? totalItems}
              </span>
            </button>

            <button
              type="button"
              data-testid="tab-pending-iqc"
              onClick={() => handleMainTabChange("pending_iqc")}
              className={emsTabClass(activeMainTab === "pending_iqc", { size: "sm" })}
            >
              <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
              <span>Chờ Kiểm IQC</span>
              <span
                className={cn(
                  "px-1.5 py-0.2 rounded-full text-[10px] font-bold tabular-nums",
                  activeMainTab === "pending_iqc"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-slate-200 text-slate-600"
                )}
              >
                {tabCounts?.pendingIqc ?? 0}
              </span>
            </button>
          </div>
        }
        headerFilters={
          activeMainTab === "qc_reports" ? (
            <div className="flex items-center gap-2 flex-wrap">
              <PageFilterTags
                items={iqcFilterTags}
                selectedKeys={!statusFilter || statusFilter === "ALL" ? [] : [statusFilter]}
                onSelect={(key) => {
                  setStatusFilter(!key || key === "ALL" ? null : key);
                  try {
                    setCurrentPage(1);
                  } catch (e) {}
                }}
                dropdownLabel="Kết quả IQC"
              />
            </div>
          ) : undefined
        }
        unitLabel="phiếu"
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs bg-white"
              onClick={() => handleExport()}
              disabled={isExporting}
            >
              <Download className="mr-2 h-3 w-3" />
              {isExporting ? "Đang xuất..." : "Xuất Excel"}
            </Button>
            {hasPermission("qcMaterials", "create") && (
              <Button
                size="sm"
                className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700"
                onClick={() => navigate({ to: "/qc-materials/create" })}
              >
                <Plus className="mr-2 h-3 w-3" /> Lập Phiếu IQC
              </Button>
            )}
          </div>
        }
        showFilter={activeMainTab === "qc_reports" && showFilter}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        filterValues={filterValues}
        filterFields={QC_MATERIAL_FILTER_FIELDS.filter((f): f is FilterField => Boolean(f && (f as any).key))}
        currentPage={activeMainTab === "pending_iqc" ? 1 : currentPage}
        pageSize={activeMainTab === "pending_iqc" ? Math.max(100, (tabCounts?.pendingIqc || 100)) : pageSize}
        totalItems={activeMainTab === "pending_iqc" ? (tabCounts?.pendingIqc || 0) : totalItems}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      >
        {activeMainTab === "pending_iqc" ? (
          <PendingIqcQueueTab />
        ) : isLoading ? (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            <span className="ml-2 text-slate-500">Đang tải dữ liệu...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full min-h-[400px] text-red-500">
            Lỗi khi tải dữ liệu. Vui lòng thử lại.
          </div>
        ) : (
          <Table className={cn("min-w-[1250px]", selectionMode && "apv-select-mode")}>
              <TableHeader className="apv-table-header">
                <TableRow>
                  <TableHead
                    className={cn("apv-col-checkbox w-10 text-center px-4 whitespace-nowrap")}
                  >
                    <DataTableSelectAllCheckbox
                      rowSelection={
                        { allSelected, toggleSelectAll, selectionMode } as any
                      }
                      asChild
                    />
                  </TableHead>
                  <TableHead className="apv-col-code font-semibold w-[160px] whitespace-nowrap">
                    Mã phiếu
                  </TableHead>
                  <TableHead className="font-semibold min-w-[240px] whitespace-nowrap">
                    Đối tượng kiểm tra
                  </TableHead>
                  <TableHead className="font-semibold min-w-[180px] whitespace-nowrap">Nguồn gốc</TableHead>
                  <TableHead className="font-semibold w-[120px] whitespace-nowrap">Ngày kiểm</TableHead>
                  <TableHead className="text-center font-semibold w-[180px] whitespace-nowrap">
                    Kết quả AQL
                  </TableHead>
                  <TableHead className="apv-col-actions w-[60px] text-right whitespace-nowrap"></TableHead>
                </TableRow>
                {showFilter && (
                  <InlineFilterRow
                    columns={QC_MATERIAL_FILTER_FIELDS as any}
                    filterFields={QC_MATERIAL_FILTER_FIELDS.filter((f): f is FilterField => Boolean(f && (f as any).key))}
                    filterValues={filterValues}
                    onFilterChange={onFilterChange}
                  />
                )}
              </TableHeader>
              <TableBody>
                {paginatedItems.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="h-32 text-center text-slate-400"
                    >
                      {statusFilter
                        ? "Không có phiếu nào ở trạng thái này"
                        : "Chưa có phiếu QC NVL nào"}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedItems.map((item) => (
                    <DataTableRow
                      key={item.id}
                      id={item.id}
                      rowSelection={
                        {
                          selectedIds,
                          handleRowMouseDown,
                          handleRowMouseUp,
                          handleRowMouseLeave,
                          wasLongPress,
                          selectionMode,
                          handleRowSelect,
                        } as any
                      }
                      onClick={(e) => {
                        if (selectionMode) return;
                        navigate({
                          to: "/qc-materials/$checkId",
                          params: { checkId: item.id },
                        });
                      }}
                    >
                      <TableCell
                        className={cn("px-4 apv-col-checkbox")}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DataTableRowCheckbox
                          id={item.id}
                          rowSelection={{ selectedIds, toggleSelect } as any}
                          asChild
                        />
                      </TableCell>
                      <TableCell className="apv-col-code py-1.5">
                        <div className="flex flex-col gap-1.5 items-start">
                          <span className="font-bold text-indigo-600 text-[13px] leading-none group-hover:underline">
                            {(item as any).materialQcId || item.id}
                          </span>
                          <StatusBadge type="QCStatus" value={item.status} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="font-semibold text-slate-900 text-[13px] leading-none">
                            {(item as any).material?.name || item.item_name}
                          </span>
                          <span className="text-[11px] text-slate-500 font-mono">
                            {(item as any).material?.materialId || (item as any).material?.code || item.item_code} • Lô: {(item as any).batchNumber || item.batch_no || "—"}
                          </span>
                          {((item as any).actualGsm || (item as any).grammage || (item as any).actualMoisture || (item as any).moisture) && (
                            <div className="flex items-center gap-1.5 text-[10px] text-slate-600 font-medium mt-0.5">
                              {((item as any).actualGsm || (item as any).grammage) && (
                                <span className="bg-slate-100 px-1 rounded text-slate-700">
                                  {(item as any).actualGsm || (item as any).grammage} g/m²
                                </span>
                              )}
                              {((item as any).actualMoisture || (item as any).moisture) && (
                                <span className="bg-slate-100 px-1 rounded text-slate-700">
                                  {(item as any).actualMoisture || (item as any).moisture}% ẩm
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 items-start">
                          <span className="text-[12px] font-medium text-slate-700 leading-none">
                            {(item as any).vendor?.name || (item as any).supplierName || item.source_name || "Nhà cung cấp"}
                          </span>
                          <Badge
                            variant="outline"
                            className="text-[10px] h-4 px-1.5 font-normal text-slate-500 border-slate-200"
                          >
                            {(item as any).purchaseOrder?.poNumber
                              ? `PO: ${(item as any).purchaseOrder.poNumber}`
                              : (item as any).materialCategory || "Nguyên vật liệu"}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-[12px] text-slate-600">
                          {(() => {
                            const d = new Date(
                              (item as any).checkTime ||
                                (item as any).check_time ||
                                item.date ||
                                (item as any).createdAt,
                            );
                            return isNaN(d.getTime())
                              ? "—"
                              : d.toLocaleDateString("vi-VN");
                          })()}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-[11px] font-semibold text-slate-700 leading-none font-mono">
                            n={(item as any).sampleSize || 0} | Maj: {(item as any).majorDefects ?? item.defects_major ?? 0} | Min: {(item as any).minorDefects ?? item.defects_minor ?? 0}
                          </span>
                          <span className="text-[10px] text-slate-400 italic leading-none">
                            Level {(item as any).aqlLevel || item.aql_level || "II"} • AQL {(item as any).aqlStandard || 1.5}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell
                        className="apv-col-actions px-4 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className="h-7 w-7 p-0 hover:bg-slate-200"
                            >
                              <MoreHorizontal className="h-4 w-4 text-slate-400" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-[185px]">
                            <DropdownMenuLabel>Thao tác</DropdownMenuLabel>
                            {hasPermission("qcMaterials", "update") && (
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate({
                                    to: "/qc-materials/$checkId/edit",
                                    params: { checkId: item.id },
                                  })
                                }
                              >
                                <Edit className="w-4 h-4 mr-2 text-slate-500" />{" "}
                                Chỉnh sửa
                              </DropdownMenuItem>
                            )}
                            {(item.status === "FAILED" ||
                              item.status === "WARNING" ||
                              item.status === "fail" ||
                              item.status === "warning") && (
                              <DropdownMenuItem
                                className="text-rose-600 focus:text-rose-700 font-semibold"
                                onClick={() => handleQuickCreateNcr(item)}
                              >
                                <AlertOctagon className="w-4 h-4 mr-2" /> + Lập Phiếu NCR
                              </DropdownMenuItem>
                            )}
                            {/* Bản in biên bản IQC nằm ở trang chi tiết (nút In biên bản) */}
                            <DropdownMenuItem
                              onClick={() => navigate({ to: "/qc-materials/$checkId", params: { checkId: item.id } })}
                            >
                              <FileText className="w-4 h-4 mr-2 text-slate-500" />{" "}
                              In biên bản
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </DataTableRow>
                  ))
                )}
              </TableBody>
            </Table>
        )}
      </ListPageShell>

      <QCMaterialForm
        open={showAddSheet}
        onOpenChange={(open) => {
          setShowAddSheet(open);
          if (!open) setEditingCheck(null);
        }}
        onSubmit={handleSave}
        initialData={editingCheck}
      />

      {activeMainTab === "qc_reports" && selectedCount > 0 && (
        <BulkActionBar
          selectedCount={selectedCount}
          onClearSelection={clearSelection}
          actions={getBulkActions()}
        />
      )}
    </>
  );
}
